/* ──────────────────────────────────────────────────────────────────────────
 * Citizen / Pilgrim Intelligence — §14.
 *
 * The brief asks for two views over one platform, and then draws the line
 * that matters:
 *
 *   "Do not expose sensitive command information to citizens."
 *
 * The obvious implementation is to hand-write a citizen payload with only
 * the safe fields in it. That is the wrong design, and the reason is worth
 * stating: it creates two places where data is assembled, so the public
 * view drifts from the operational one and every new field is a fresh
 * chance to leak. The leak is never in the field somebody thought about.
 *
 * So the pilgrim view is DERIVED. It takes the same operational objects the
 * command centre uses and runs them through `redactFor("PUBLIC")` — the
 * enforced filter from §17 — then shapes what survives. A field can only
 * reach a pilgrim if it survives a filter that deletes rather than hides.
 * The test suite asserts the negative directly: it scans the rendered
 * output for restricted terms.
 *
 * ── THE SECOND DESIGN DECISION ──
 * A pilgrim is told what to DO, not what the command centre KNOWS.
 * "Ramkund is very crowded — Panchavati Ghat is 12 minutes away and open"
 * is useful. "Ramkund at 4.2 persons/m², command tier Gold, QRT Alpha
 * deployed" is both frightening and operationally sensitive. The
 * translation from state to advice happens here, deliberately, once.
 * ────────────────────────────────────────────────────────────────────────── */

import { redactFor, canAccess } from "./privacy";
import { ASSEMBLY_AREA_M2 } from "../geo/services/crowdSafety";
import { buildTwin } from "../twin/twin";

/* Public crowd wording. Deliberately coarse: a pilgrim needs to choose
 * between ghats, not to read a density figure. Publishing the number
 * invites people to compare it with a number they saw elsewhere and
 * conclude the government is lying to them. */
const PUBLIC_CROWD_LEVEL = [
  { maxDensity: 1.0, id: "comfortable", label: { en: "Comfortable",  mr: "मोकळे",        hi: "आरामदायक" },
    advice: { en: "Good time to visit.", mr: "जाण्यासाठी चांगली वेळ.", hi: "जाने का अच्छा समय।" } },
  { maxDensity: 2.0, id: "busy",        label: { en: "Busy",         mr: "गजबजलेले",     hi: "व्यस्त" },
    advice: { en: "Allow extra time. Keep your group together.", mr: "जास्त वेळ ठेवा. गटासोबत रहा.", hi: "अतिरिक्त समय रखें। समूह के साथ रहें।" } },
  { maxDensity: 3.0, id: "very_busy",   label: { en: "Very busy",    mr: "खूप गर्दी",     hi: "बहुत भीड़" },
    advice: { en: "Consider an alternative ghat or a later time.", mr: "पर्यायी घाट किंवा नंतरची वेळ निवडा.", hi: "वैकल्पिक घाट या बाद का समय चुनें।" } },
  { maxDensity: Infinity, id: "restricted", label: { en: "Entry restricted", mr: "प्रवेश मर्यादित", hi: "प्रवेश सीमित" },
    advice: { en: "Please use an alternative ghat. Follow officers on the ground.", mr: "कृपया पर्यायी घाट वापरा. अधिकाऱ्यांच्या सूचना पाळा.", hi: "कृपया वैकल्पिक घाट का उपयोग करें। अधिकारियों का पालन करें।" } },
];

function publicCrowdLevel(density) {
  if (density == null) {
    return {
      id: "unknown",
      label: { en: "Information unavailable", mr: "माहिती उपलब्ध नाही", hi: "जानकारी उपलब्ध नहीं" },
      advice: {
        en: "Crowd information is not available for this location. Follow officers on the ground.",
        mr: "या ठिकाणासाठी माहिती उपलब्ध नाही. अधिकाऱ्यांच्या सूचना पाळा.",
        hi: "इस स्थान की जानकारी उपलब्ध नहीं है। अधिकारियों का पालन करें।",
      },
    };
  }
  return PUBLIC_CROWD_LEVEL.find((l) => density <= l.maxDensity);
}

/* Facility types a pilgrim may be shown. Command assets — police posts,
 * command centres, unit positions — are absent from this list by design,
 * and the omission is enforced downstream by the redaction pass as well. */
const PUBLIC_FACILITY_TYPES = [
  "hospital", "medicalCamp", "waterStation", "toiletCluster",
  "railwayStation", "busTerminal", "parking", "ghat",
];

/* ── The pilgrim projection ────────────────────────────────────────────── */
export function buildPilgrimView(liveState, { lang = "mr", twin = null, advisories = [] } = {}) {
  const t = twin ?? buildTwin(liveState);

  /* Purpose is declared and checked, not assumed, so a future caller
   * cannot quietly reuse this for something else. */
  const gate = canAccess("PUBLIC", "sector_state", "PUBLIC_INFORMATION");
  if (!gate.allowed) {
    return { available: false, reason: gate.reason };
  }

  /* Ghat status. Derived from the same sector state the command centre
   * reads, then coarsened. */
  const ghats = t.byType("ghat").map((g) => {
    const sector = liveState.sectors.find((s) => s.id === g.sector);
    const area = ASSEMBLY_AREA_M2[g.sector];
    const density = sector && area ? sector.current / area : null;
    const level = publicCrowdLevel(density);
    return {
      id: g.id,
      name: g.name,
      coord: g.coord,
      crowdLevel: level.id,
      crowdLabel: level.label,
      advice: level.advice,
      /* Deliberately absent: density, occupancy percentage, capacity,
       * command tier, forecast, deployment. */
      open: level.id !== "restricted",
    };
  });

  /* Suggest a genuinely better alternative rather than just listing them.
   * "Go somewhere else" without saying where is not advice. */
  const openGhats = ghats.filter((g) => g.open);
  const calmest = [...ghats].sort(
    (a, b) => PUBLIC_CROWD_LEVEL.findIndex((l) => l.id === a.crowdLevel)
            - PUBLIC_CROWD_LEVEL.findIndex((l) => l.id === b.crowdLevel),
  )[0] ?? null;

  const facilities = t.all()
    .filter((n) => PUBLIC_FACILITY_TYPES.includes(n.type) && n.coord)
    .map((n) => ({
      id: n.id, name: n.name, type: n.type, coord: n.coord,
      /* No capacity, no occupancy, no bed counts. A pilgrim needs to know
       * where the medical camp is, not how full it is — and publishing
       * that a hospital is at capacity causes people to avoid seeking
       * care. */
    }));

  const view = {
    available: true,
    lang,
    ghats,
    recommendedGhat: calmest && calmest.open ? calmest : null,
    openGhatCount: openGhats.length,
    facilities,
    /* Only advisories explicitly marked for publication. An internal
     * recommendation is never shown merely because it exists. */
    advisories: advisories
      .filter((a) => a.published === true)
      .map((a) => ({ id: a.id, message: a.message, issuedAt: a.issuedAt })),
    emergency: {
      police: "112",
      medical: "108",
      helpline: { en: "Kumbh helpline", mr: "कुंभ मदत क्रमांक", hi: "कुंभ हेल्पलाइन" },
      lostAndFound: t.all()
        .filter((n) => n.id?.startsWith("HELP-"))
        .map((n) => ({ id: n.id, name: n.name, coord: n.coord })),
    },
    /* Said plainly to the public too, not only to officers. A citizen
     * acting on a simulated crowd level deserves to know it is simulated. */
    notice: {
      en: "Crowd information is indicative and generated by a demonstration system. Always follow instructions from officers on the ground.",
      mr: "गर्दीची माहिती केवळ अंदाजे आहे आणि प्रात्यक्षिक प्रणालीद्वारे तयार केली आहे. नेहमी अधिकाऱ्यांच्या सूचना पाळा.",
      hi: "भीड़ की जानकारी केवल संकेतात्मक है और एक प्रदर्शन प्रणाली द्वारा बनाई गई है। हमेशा अधिकारियों के निर्देशों का पालन करें।",
    },
  };

  /* The enforced pass. Anything classified above PUBLIC that slipped into
   * the shape above is deleted here rather than trusted to have been left
   * out. `redacted` is returned so a reviewer can see the filter is doing
   * work rather than passing everything through. */
  const { data, removed } = redactFor("PUBLIC", view);
  return { ...data, redactedFields: removed };
}

/* Terms that must never appear in a pilgrim-facing payload. Used by the
 * test suite to scan the rendered output — a denial list is a weaker
 * control than the redaction pass, but it catches the case where someone
 * adds a field with a name the classifier does not know.
 *
 * Note what is deliberately NOT on this list. "Officer" appears in the
 * public advice — "follow officers on the ground" — and that is correct:
 * telling a pilgrim to obey the police is the single most useful thing
 * this view can say. The prohibition is on officer IDENTITY and
 * DEPLOYMENT, not on the word. A denial list blunt enough to ban the word
 * would have forced the advice to be deleted, which is how a safety
 * control ends up making people less safe.
 *
 * "Entry restricted" is likewise a legitimate public ghat status, distinct
 * from the RESTRICTED classification marker. */
export const FORBIDDEN_PUBLIC_TERMS = [
  // Command assets and deployment
  "police post", "commandcentre", "command centre", "qrt", "ndrf",
  "fire station", "deployment", "strength", "unit-", "cmd-", "pol-",
  // Officer identity
  "authorisedby", "officername", "officerid", "operatorid",
  // Operational figures a pilgrim must not be given
  "commandtier", "ingressegressratio", "egresscapacity",
  "bedsfree", "emergencyfree", "occupancypct", "density",
  "advancedlifesupport", "netpermin", "capacity",
];

export const PILGRIM_ASSUMPTIONS = [
  "The pilgrim view is derived from the operational data and passed through the enforced §17 redaction filter. It is not a separately assembled payload, because two assembly points means two places to leak from.",
  "Crowd state is published as a coarse band with advice, never as a density figure. A pilgrim needs to choose between ghats, not to read a number.",
  "Facility locations are published; facility capacity is not. Publishing that a hospital is at capacity causes people to avoid seeking care.",
  "Only advisories explicitly marked published are shown. An internal recommendation is never surfaced merely because it exists.",
  "The public notice states that the information is simulated. A citizen acting on a demonstration figure is entitled to know that.",
];
